/**
 * photo_preview_controller.js — show the photos picked in a file input as thumbnails.
 *
 * A file input on its own only says "2 files chosen", so a customer cannot see
 * which photos they are about to send. Each change replaces the thumbnails with
 * the files now in the input, drawn from object URLs, so nothing is uploaded to
 * show them. A photo this browser cannot draw (HEIC outside Safari) shows as its
 * file name instead of a broken image.
 *
 * Object URLs hold the file in memory until revoked, so each pick revokes the
 * previous set, and disconnect() revokes the last.
 *
 * Wired with data-controller="photo-preview" on an element wrapping the input
 * (data-photo-preview-target="input",
 * data-action="change->photo-preview#show") and an empty container
 * (data-photo-preview-target="list").
 */

import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
    static targets = ["input", "list"];

    connect() {
        this._urls = [];
    }

    disconnect() {
        this._clear();
    }

    show() {
        this._clear();
        for (const file of this.inputTarget.files) {
            this.listTarget.append(this._thumbnail(file));
        }
    }

    _thumbnail(file) {
        const url = URL.createObjectURL(file);
        this._urls.push(url);
        const img = document.createElement("img");
        img.className = "profile-photo-preview";
        img.alt = file.name;
        img.src = url;
        img.addEventListener("error", () => {
            const name = document.createElement("span");
            name.className = "profile-photo-preview profile-photo-preview--name";
            name.textContent = file.name;
            img.replaceWith(name);
        }, { once: true });
        return img;
    }

    _clear() {
        this._urls.forEach((url) => URL.revokeObjectURL(url));
        this._urls = [];
        this.listTarget.replaceChildren();
    }
}
